import React, { useEffect, useState } from "react";
import { Plus, X, Building2, BookOpen, MapPin } from "lucide-react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import GeoAddress from "./GeoAddress";
import dialCodes from "../lib/dialCodes";

const blankCampus = () => ({ name: "", country: "", country_code: "", state: "", state_id: "", city: "", zip: "", line1: "", line2: "", email: "", phone: "", courses: [] });

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const dialFor = (iso2) => dialCodes[(iso2 || "").toUpperCase()] || "";
export const emailValid = (v) => !v || EMAIL_RX.test(v.trim());
export const phoneValid = (phone, iso2) => {
  const v = (phone || "").trim();
  if (!v) return true;
  const dial = dialFor(iso2);
  if (dial && !v.replace(/\s/g, "").startsWith(dial.replace(/\s/g, ""))) return false;
  return v.replace(/[^\d]/g, "").length >= 7;
};

// Returns an array of human-readable validation errors for a list of campuses.
export const validateCampuses = (campuses) => {
  const errs = [];
  (campuses || []).forEach((c, i) => {
    const label = c.name || `Campus ${i + 1}`;
    if (!emailValid(c.email)) errs.push(`${label}: invalid email address`);
    if (!phoneValid(c.phone, c.country_code)) {
      const dial = dialFor(c.country_code);
      errs.push(`${label}: telephone must be a valid number${dial ? ` starting with ${dial}` : ""}`);
    }
  });
  return errs;
};

// Controlled editor for an array of campuses (used by Universities & Institutions masters)
export default function CampusEditor({ value = [], onChange }) {
  const [courseMaster, setCourseMaster] = useState([]);
  const [fieldMaster, setFieldMaster] = useState([]);
  const [streamMaster, setStreamMaster] = useState([]);

  useEffect(() => {
    api.get("/academics/edu-courses").then((r) => setCourseMaster(r.data)).catch(() => {});
    api.get("/academics/fields").then((r) => setFieldMaster(r.data)).catch(() => {});
    api.get("/academics/streams").then((r) => setStreamMaster(r.data)).catch(() => {});
  }, []);

  const levels = [...new Set((courseMaster || []).map((c) => c.level).filter(Boolean))];
  const campuses = value.length ? value : [blankCampus()];

  const setCampus = (i, patch) => onChange(campuses.map((c, idx) => {
    if (idx !== i) return c;
    const merged = { ...c, ...patch };
    if (patch.city && patch.city !== c.city && !(c.name || "").trim()) merged.name = `${patch.city} Campus`;
    // Prefill the telephone with the country's ISD code when the country changes.
    if (patch.country_code && patch.country_code !== c.country_code) {
      const newDial = dialFor(patch.country_code);
      const oldDial = dialFor(c.country_code);
      const cur = (merged.phone || "").trim();
      if (newDial && (!cur || cur === oldDial)) merged.phone = `${newDial} `;
    }
    return merged;
  }));
  const addCampus = () => onChange([...campuses, blankCampus()]);
  const removeCampus = (i) => onChange(campuses.filter((_, idx) => idx !== i));

  const setCourse = (ci, coi, patch) => setCampus(ci, { courses: (campuses[ci].courses || []).map((c, idx) => idx === coi ? { ...c, ...patch } : c) });
  const addCourse = (ci) => setCampus(ci, { courses: [...(campuses[ci].courses || []), { name: "", level: "", stream: "", fields: [] }] });
  const removeCourse = (ci, coi) => setCampus(ci, { courses: campuses[ci].courses.filter((_, idx) => idx !== coi) });

  const streamsFor = (lvl) => {
    const list = (streamMaster || []).filter((s) => !s.level || s.level === lvl);
    return (list.length ? list : streamMaster).map((s) => s.name);
  };
  const coursesFor = (lvl, stream) => {
    let list = (courseMaster || []).filter((c) => (!lvl || c.level === lvl) && (!stream || (c.stream || "").toLowerCase() === stream.toLowerCase()));
    if (!list.length) list = (courseMaster || []).filter((c) => !lvl || c.level === lvl);
    if (!list.length) list = courseMaster || [];
    return [...new Set(list.map((c) => c.course))].sort();
  };

  const sel = "h-9 rounded-md border border-input bg-background px-2 text-sm";

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-[#012a1e] flex items-center gap-1.5"><MapPin size={16} /> Campuses</h3>
        <Button type="button" variant="outline" size="sm" onClick={addCampus} data-testid="add-campus-btn" className="rounded-full"><Plus size={15} className="mr-1" /> Add campus</Button>
      </div>
      <div className="space-y-4">
        {campuses.map((camp, ci) => (
          <div key={ci} className="rounded-xl border border-slate-200 p-4 bg-[#f9fcfb]" data-testid={`campus-${ci}`}>
            <div className="flex items-center gap-2 mb-3">
              <Building2 size={16} className="text-[#004030]" />
              <Input value={camp.name} onChange={(e) => setCampus(ci, { name: e.target.value })} placeholder="Campus name (auto-fills from city)" className="h-10 flex-1" data-testid={`campus-name-${ci}`} />
              {campuses.length > 1 && <button onClick={() => removeCampus(ci)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`remove-campus-${ci}`}><X size={18} /></button>}
            </div>
            <GeoAddress value={camp} onChange={(v) => setCampus(ci, v)} prefix={`campus-${ci}`} />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Email</Label>
                <Input type="email" value={camp.email || ""} onChange={(e) => setCampus(ci, { email: e.target.value })} placeholder="campus@uni.edu" className={`h-10 ${!emailValid(camp.email) ? "border-red-400" : ""}`} data-testid={`campus-email-${ci}`} />
                {!emailValid(camp.email) && <p className="text-[11px] text-red-500" data-testid={`campus-email-err-${ci}`}>Enter a valid email address.</p>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Telephone{dialFor(camp.country_code) ? <span className="text-slate-400"> (ISD {dialFor(camp.country_code)})</span> : ""}</Label>
                <Input value={camp.phone || ""} onChange={(e) => setCampus(ci, { phone: e.target.value })} placeholder={dialFor(camp.country_code) ? `${dialFor(camp.country_code)} 12 345 6789` : "Select country first"} className={`h-10 ${!phoneValid(camp.phone, camp.country_code) ? "border-red-400" : ""}`} data-testid={`campus-phone-${ci}`} />
                {!phoneValid(camp.phone, camp.country_code) && <p className="text-[11px] text-red-500" data-testid={`campus-phone-err-${ci}`}>Must start with {dialFor(camp.country_code) || "the country ISD code"} and be a valid number.</p>}
              </div>
            </div>

            <div className="mt-3 border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-[#012a1e] flex items-center gap-1.5"><BookOpen size={14} /> Courses offered</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => addCourse(ci)} data-testid={`add-course-${ci}`} className="text-[#004030] h-7"><Plus size={14} className="mr-1" /> Course</Button>
              </div>
              {(camp.courses || []).map((co, coi) => (
                <div key={coi} className="rounded-lg border border-slate-200 bg-white p-2.5 mb-2" data-testid={`course-${ci}-${coi}`}>
                  <div className="flex gap-2 items-start">
                    <div className="flex-1 space-y-1.5">
                      <div className="grid grid-cols-3 gap-2">
                        <select value={co.level || ""} onChange={(e) => setCourse(ci, coi, { level: e.target.value, stream: "", name: "" })} className={sel} data-testid={`course-level-${ci}-${coi}`}>
                          <option value="">Level…</option>
                          {levels.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                        <select value={co.stream || ""} onChange={(e) => setCourse(ci, coi, { stream: e.target.value, name: "" })} className={sel} data-testid={`course-stream-${ci}-${coi}`}>
                          <option value="">Stream…</option>
                          {streamsFor(co.level).map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select value={co.name || ""} onChange={(e) => setCourse(ci, coi, { name: e.target.value })} className={sel} data-testid={`course-name-${ci}-${coi}`}>
                          <option value="">Course…</option>
                          {coursesFor(co.level, co.stream).map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                      <div>
                        <p className="text-[11px] text-slate-400 mb-1">Fields / specialisations (from master — click to toggle)</p>
                        <div className="flex flex-wrap gap-1.5" data-testid={`course-fields-${ci}-${coi}`}>
                          {fieldMaster.map((fl) => {
                            const on = (co.fields || []).includes(fl.name);
                            return <button key={fl.id} type="button" onClick={() => { const cur = co.fields || []; setCourse(ci, coi, { fields: on ? cur.filter((x) => x !== fl.name) : [...cur, fl.name] }); }}
                              className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${on ? "bg-[#004030] text-white border-[#004030]" : "bg-white text-slate-600 border-slate-300 hover:border-[#004030]"}`}>{fl.name}</button>;
                          })}
                          {!fieldMaster.length && <span className="text-[11px] text-slate-400">Add fields under the Fields master.</span>}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => removeCourse(ci, coi)} className="text-slate-400 hover:text-red-600 p-1"><X size={16} /></button>
                  </div>
                </div>
              ))}
              {!(camp.courses || []).length && <p className="text-xs text-slate-400">No courses yet — add the programmes this campus offers.</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export { blankCampus };
